// fallback.js: a tiny stand-in world (same contract) used when a real world
// fails to load or throws. Cheap: one full-screen shader, a soft glow in the
// chapter's colours and a procedural star field. It still answers to hold,
// progress and shiftX/shiftY, so the film never goes dead.
import * as THREE from 'three';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';

const TINTS = {
  nebula: [[0.020, 0.008, 0.050], [0.55, 0.22, 0.95]],
  earth: [[0.004, 0.010, 0.030], [0.30, 0.55, 1.00]],
  galaxy: [[0.020, 0.012, 0.006], [1.00, 0.72, 0.38]],
  blackhole: [[0.010, 0.004, 0.002], [1.00, 0.50, 0.16]],
};

const FRAG = /* glsl */`
precision highp float;
varying vec2 vUv;
uniform float uTime, uP, uHold, uAspect;
uniform vec2 uShift;
uniform vec3 uA, uB;
float h21(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
void main() {
  vec2 c = vec2(0.5 + uShift.x, 0.5 + uShift.y);
  vec2 d = (vUv - c) * vec2(uAspect, 1.0);
  float r = length(d);
  float breathe = 0.85 + 0.15 * sin(uTime * 0.4);
  vec3 col = uA + uB * 0.12 * exp(-r * r * 2.2) * breathe;
  col += uB * exp(-r * r * 40.0) * (0.15 + 2.5 * uHold * uHold) * (0.6 + 0.4 * uP);
  for (int k = 0; k < 3; k++) {
    float sc = 60.0 + float(k) * 70.0;
    vec2 g = vUv * vec2(uAspect, 1.0) * sc + float(k) * 13.1;
    vec2 id = floor(g), f = fract(g) - 0.5;
    float s = h21(id);
    vec2 o = vec2(h21(id + 1.7), h21(id + 9.3)) - 0.5;
    float tw = 0.65 + 0.35 * sin(uTime * (1.0 + s * 3.0) + s * 40.0);
    float star = step(0.975, s) * exp(-dot(f - o * 0.7, f - o * 0.7) * 900.0) * tw;
    col += vec3(0.9, 0.95, 1.0) * star * (1.8 - float(k) * 0.5);
  }
  gl_FragColor = vec4(col, 1.0);
}`;

export function createFallback(renderer, name) {
  const t = TINTS[name] || TINTS.nebula;
  const mat = new THREE.ShaderMaterial({
    depthTest: false, depthWrite: false,
    uniforms: {
      uTime: { value: 0 }, uP: { value: 0 }, uHold: { value: 0 }, uAspect: { value: 1 },
      uShift: { value: new THREE.Vector2() },
      uA: { value: new THREE.Vector3(...t[0]) }, uB: { value: new THREE.Vector3(...t[1]) },
    },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
    fragmentShader: FRAG,
  });
  const quad = new FullScreenQuad(mat);
  const u = mat.uniforms;
  return {
    isFallback: true,
    grade: { exposure: 1, bloomK: 0.7 },
    anchors: {},
    async load() {},
    resize(w, h) { u.uAspect.value = w / Math.max(1, h); },
    update(dt, time, input) {
      u.uTime.value = time;
      u.uP.value = input.progress || 0;
      u.uHold.value = input.hold || 0;
      const p = input.params || {};
      u.uShift.value.set(p.shiftX || 0, p.shiftY || 0);
    },
    render(target) { renderer.setRenderTarget(target); quad.render(renderer); },
    dispose() { mat.dispose(); quad.dispose(); },
  };
}
